import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import { OrderStorage, StoredOrder } from '../../services/orderStorage';
import Notification, { NotificationType } from '../../features/notifications/Notification';

interface OrderStatusProps {
  orgUrl: string;
  tableId: string;
}

interface OrderStatusData {
  orderId: string;
  orderNumber: string;
  status: string;
  customerName: string;
  total: number;
  timestamp: string;
  tableName: string;
  items: any[];
  notes: string;
}

const OrderStatus: React.FC<OrderStatusProps> = ({ orgUrl, tableId }) => {
  const [storedOrders, setStoredOrders] = useState<StoredOrder[]>([]);
  const [orderStatuses, setOrderStatuses] = useState<Map<string, OrderStatusData>>(new Map());
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{ type: NotificationType; message: string } | null>(null);

  // Load stored orders on component mount and refresh periodically
  useEffect(() => {
    const loadOrders = () => {
      const orders = OrderStorage.getOrdersForTable(orgUrl, tableId);
      setStoredOrders(orders);
    };

    // Load orders immediately
    loadOrders();

    // Listen for order storage changes
    const handleOrderStorageChange = (event: CustomEvent) => {
      const { action, order } = event.detail;
      if (action === 'add' && order.orgUrl === orgUrl && order.tableId === tableId) {
        loadOrders();
      }
    };

    window.addEventListener('orderStorageChanged', handleOrderStorageChange as EventListener);

    // Refresh orders every 5 seconds to catch new orders
    const interval = setInterval(loadOrders, 5000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('orderStorageChanged', handleOrderStorageChange as EventListener);
    };
  }, [orgUrl, tableId]);

  // Poll for order status updates every 30 seconds
  useEffect(() => {
    if (storedOrders.length === 0) return;

    const pollOrderStatuses = async () => {
      setLoading(true);
      const newStatuses = new Map<string, OrderStatusData>();
      const removedIds: string[] = [];

      try {
        for (const storedOrder of storedOrders) {
          try {
            const response = await apiService.getPublicOrderStatus(orgUrl, storedOrder.orderId);
            if (response.success && response.data) {
              const status = (response.data.status || '').toString().toLowerCase();
              // If order is rejected/cancelled/removed on the server, drop it from local storage
              if (status === 'rejected' || status === 'canceled' || status === 'cancelled') {
                OrderStorage.removeOrder(storedOrder.orderId);
                removedIds.push(storedOrder.orderId);
                continue;
              }
              newStatuses.set(storedOrder.orderId, response.data);
            } else {
              const err = (response as any)?.error?.toString().toLowerCase?.() || '';
              if (err.includes('not found') || err.includes('deleted') || err.includes('rejected')) {
                OrderStorage.removeOrder(storedOrder.orderId);
                removedIds.push(storedOrder.orderId);
              }
            }
          } catch (e: any) {
            // If API throws 404-like errors, remove as well
            const msg = (e?.message || '').toString().toLowerCase();
            if (msg.includes('404') || msg.includes('not found')) {
              OrderStorage.removeOrder(storedOrder.orderId);
              removedIds.push(storedOrder.orderId);
            }
          }
        }
        setOrderStatuses(newStatuses);
        if (removedIds.length > 0) {
          setStoredOrders(prev => prev.filter(o => !removedIds.includes(o.orderId)));
        }
      } catch (error) {
        console.error('Error polling order statuses:', error);
        setNotification({ 
          type: 'error', 
          message: 'Failed to update order status. Please refresh the page.' 
        });
      } finally {
        setLoading(false);
      }
    };

    // Initial poll
    pollOrderStatuses();

    // Set up interval for polling
    const interval = setInterval(pollOrderStatuses, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [storedOrders, orgUrl]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#ff9500';
      case 'preparing':
        return '#007aff';
      case 'ready':
        return '#30d158';
      case 'completed':
        return '#8e8e93';
      default:
        return '#8e8e93';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'preparing':
        return 'Preparing';
      case 'ready':
        return 'Ready for Pickup';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  };

  const formatTime = (timestamp: string | number) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const removeOrder = (orderId: string) => {
    OrderStorage.removeOrder(orderId);
    setStoredOrders(prev => prev.filter(order => order.orderId !== orderId));
    setOrderStatuses(prev => {
      const newMap = new Map(prev);
      newMap.delete(orderId);
      return newMap;
    });
  };

  // Don't show anything if no orders
  if (storedOrders.length === 0) {
    return null;
  }

  return (
    <div className="order-status-section">
      {/* Notification Display */}
      {notification && (
        <div style={{ 
          position: 'fixed', 
          top: '20px', 
          right: '20px', 
          zIndex: 1000,
          maxWidth: '400px'
        }}>
          <Notification
            type={notification.type}
            message={notification.message}
            onClose={() => setNotification(null)}
            autoClose={true}
            duration={4000}
          />
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            Your Orders
            {loading && (
              <span style={{ 
                fontSize: '0.8rem', 
                color: '#666', 
                marginLeft: '0.5rem',
                fontStyle: 'italic'
              }}>
                Updating...
              </span>
            )}
          </h3>
        </div>
        
        <div className="order-list">
          {storedOrders.map(storedOrder => {
            const statusData = orderStatuses.get(storedOrder.orderId);
            const status = statusData?.status || 'pending';
            const isCompleted = status === 'completed';
            
            return (
              <div key={storedOrder.orderId} className="order-item-status">
                <div className="order-header">
                  <div className="order-info">
                    <h4>Order #{storedOrder.orderNumber}</h4>
                    <p className="order-time">
                      Placed at {formatTime(storedOrder.timestamp)}
                    </p>
                    <p className="order-customer">
                      {storedOrder.customerName}
                    </p>
                  </div>
                  
                  <div className="order-status">
                    <div 
                      className="status-badge"
                      style={{ 
                        backgroundColor: getStatusColor(status),
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '20px',
                        fontSize: '0.875rem',
                        fontWeight: '600'
                      }}
                    >
                      {getStatusText(status)}
                    </div>
                    
                    {statusData && (
                      <p className="order-total">
                        ${statusData.total.toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>

                {statusData && statusData.items && statusData.items.length > 0 && (
                  <div className="order-items">
                    <h5>Items:</h5>
                    <ul>
                      {statusData.items.map((item, index) => (
                        <li key={index}>
                          {item.quantity}x {item.menuItem?.name || 'Unknown Item'}
                          {item.specialRequests && (
                            <span className="special-requests">
                              - {item.specialRequests}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {statusData?.notes && (
                  <div className="order-notes">
                    <strong>Notes:</strong> {statusData.notes}
                  </div>
                )}

                {isCompleted && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => removeOrder(storedOrder.orderId)}
                    style={{ 
                      marginTop: '1rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem 1rem'
                    }}
                  >
                    Remove Order
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .order-status-section {
          margin-bottom: 2rem;
        }
        
        .order-list {
          max-height: 400px;
          overflow-y: auto;
        }
        
        .order-item-status {
          border: 1px solid #e5e5e7;
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 1rem;
          background: white;
        }
        
        .order-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
        }
        
        .order-info h4 {
          margin: 0 0 0.5rem 0;
          color: #1d1d1f;
          font-size: 1.125rem;
        }
        
        .order-time {
          margin: 0 0 0.25rem 0;
          color: #666;
          font-size: 0.875rem;
        }
        
        .order-customer {
          margin: 0;
          color: #1d1d1f;
          font-weight: 500;
        }
        
        .order-status {
          text-align: right;
        }
        
        .order-total {
          margin: 0.5rem 0 0 0;
          font-weight: 600;
          color: #1d1d1f;
        }
        
        .order-items {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #e5e5e7;
        }
        
        .order-items h5 {
          margin: 0 0 0.5rem 0;
          color: #1d1d1f;
          font-size: 1rem;
        }
        
        .order-items ul {
          margin: 0;
          padding-left: 1.5rem;
        }
        
        .order-items li {
          margin-bottom: 0.25rem;
          color: #1d1d1f;
        }
        
        .special-requests {
          color: #666;
          font-style: italic;
        }
        
        .order-notes {
          margin-top: 1rem;
          padding: 0.75rem;
          background: #f5f5f7;
          border-radius: 8px;
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  );
};

export default OrderStatus; 